'use client';

import { X, Clock, Sparkles } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface RateLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
}

export function RateLimitModal({ isOpen, onClose, isLoggedIn }: RateLimitModalProps) {
  const router = useRouter();

  if (!isOpen) return null;

  const handleViewPlans = () => {
    onClose();
    router.push('/pricing');
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
      animation: 'fadeIn 0.3s ease-out',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--color-bg-primary), var(--color-bg-secondary))',
        borderRadius: 'var(--radius-2xl)',
        padding: '2.5rem',
        maxWidth: '500px',
        width: '100%',
        boxShadow: 'var(--shadow-xl-colored)',
        border: '2px solid var(--color-border-light)',
        position: 'relative',
        animation: 'scaleIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(168, 216, 230, 0.15)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(168, 216, 230, 0.3)';
            e.currentTarget.style.transform = 'rotate(90deg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(168, 216, 230, 0.15)';
            e.currentTarget.style.transform = 'rotate(0deg)';
          }}
        >
          <X size={20} />
        </button>

        {/* Icon */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '1.5rem',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #F8C8A0, #A8D8E6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(248, 200, 160, 0.4)',
          }}>
            <Clock size={40} color="white" />
          </div>
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: 800,
          textAlign: 'center',
          marginBottom: '1rem',
          background: 'linear-gradient(45deg, var(--color-primary), var(--color-accent))',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          You've Used Your Free Demo!
        </h2>

        {/* Message */}
        <p style={{
          textAlign: 'center',
          fontSize: '1.1rem',
          color: 'var(--color-text-secondary)',
          marginBottom: '2rem',
          lineHeight: 1.6,
        }}>
          {isLoggedIn 
            ? "You've already created your free demo book today. Unlock unlimited creations with our plans!"
            : "You've reached your free demo limit for today. Sign in or check out our plans to keep creating!"}
        </p>

        {/* Benefits */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.1), rgba(201, 182, 228, 0.1))',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid rgba(168, 216, 230, 0.3)',
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 700,
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}>
            <Sparkles size={20} style={{ color: 'var(--color-primary)' }} />
            <span>Get More with Our Plans:</span>
          </h3>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
              <span style={{ color: 'var(--color-success)', fontSize: '1.25rem' }}>✓</span>
              <span>13-page full books with no watermarks</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
              <span style={{ color: 'var(--color-success)', fontSize: '1.25rem' }}>✓</span>
              <span>High-quality PDF downloads</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
              <span style={{ color: 'var(--color-success)', fontSize: '1.25rem' }}>✓</span>
              <span>Unlimited books with subscription</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          flexDirection: 'column',
        }}>
          <button
            onClick={handleViewPlans}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #A8D8E6, #C9B6E4, #F8C8A0)',
              backgroundSize: '200% 200%',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              fontSize: '1.125rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(168, 216, 230, 0.4)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(168, 216, 230, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(168, 216, 230, 0.4)';
            }}
          >
            View Plans
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '0.875rem 2rem',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '2px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
              e.currentTarget.style.color = 'var(--color-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
          >
            Maybe Later
          </button>
        </div>
      </div>

      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

